"use client"

import { Skeleton } from "@/app/(site)/_components/Atoms/Skeleton"
import CentraCategoryLinksContentCard from "@/app/(site)/_components/Organisms/Cards/CentraCategoryLinksContentCard"
import { LoadMoreCard } from "@/app/(site)/_components/Organisms/Cards/LoadMoreCard"
import SubBigProductCard from "@/app/(site)/_components/Organisms/Cards/ProductCards/BigProductCard"
import RegularProductCard from "@/app/(site)/_components/Organisms/Cards/ProductCards/RegularProductCard"
import DesktopFilter from "@/app/(site)/_components/Organisms/ProductGrids/Filter/DesktopFilter"
import MobileFilter from "@/app/(site)/_components/Organisms/ProductGrids/Filter/Mobile/MobileFilter"
import { filterReducer } from "@/app/(site)/_components/Organisms/ProductGrids/filterReducer"
import {
    SWRgetDepictProductsInListingViaExternalId,
    updateQueryParamsWithSelectedOptions
} from "@/app/(site)/_components/Organisms/ProductGrids/helpers"
import { sortConfig } from "@/app/(site)/_components/Organisms/ProductGrids/types/options"
import { debounce } from "@/app/(site)/_utils/debounce"
import { getLangCode } from "@/app/(site)/_utils/misc"
import { depictMerchant } from "@/data/depict-merchant"
import { GetListingResponse } from "@/lib/depict-client/types/api/GetListingResponse"
import { SearchFilter } from "@/lib/depict-client/types/api/ProductListingResponseV3"
import { isClient } from "@/lib/env-utils/env-utils"
import logger from "@/lib/logger/logger-init"
import { MinimizedCentraProduct } from "@/types/centra/checkout-api/products/post-products"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import React, { Fragment, useEffect, useMemo, useReducer, useRef } from "react"
import toast from "react-hot-toast"
import useSWRInfinite from "swr/infinite"

type ProductGridFetcherProps = {
    groupedProducts: { key: string; items: MinimizedCentraProduct[] }[]
    initialCursor: string
    externalId: string | undefined
    nextPageUrl: string
    initialFilterQuery: any
    nHits: number
    selectedCategoryChildren?: GetListingResponse
    loadMoreLimit: number
    params: { locale: string; category: string; subCategory: string[] }
    initialFilters: SearchFilter[]
    showFilter?: boolean
    marketCode: string
    localeCode: string
    depictSessionId: string
    fetchLimit: number
}

export default function ProductGridFetcher({
    groupedProducts,
    initialCursor,
    externalId,
    nextPageUrl,
    selectedCategoryChildren,
    initialFilterQuery,
    loadMoreLimit,
    params,
    nHits,
    showFilter,
    initialFilters,
    marketCode,
    localeCode,
    depictSessionId,
    fetchLimit
}: ProductGridFetcherProps) {
    const [filterState, dispatch] = useReducer(filterReducer, initialFilterQuery)
    const prevFilterStateRef = useRef(filterState)
    const router: any = useRouter()
    const pathname = usePathname()

    const centraLangCode = getLangCode(params.locale) || ""

    const getKey = (
        pageIndex: number,
        previousPageData: { cursor: string | undefined }
    ) => {
        if (pageIndex > 0 && (!previousPageData || !previousPageData.cursor)) return null

        const cursor = pageIndex === 0 ? undefined : previousPageData?.cursor
        const initialLoadLimit = pageIndex === 0 ? fetchLimit : loadMoreLimit

        return [
            "fetchProducts",
            externalId,
            depictMerchant,
            marketCode,
            localeCode,
            depictSessionId,
            JSON.stringify(filterState.filters),
            initialLoadLimit,
            filterState.sort ? JSON.stringify(filterState.sort) : null,
            cursor
        ]
    }

    const {
        data,
        size,
        setSize,
        isValidating,
        isLoading = false,
        mutate
    } = useSWRInfinite(getKey, SWRgetDepictProductsInListingViaExternalId, {
        revalidateOnFocus: false,
        initialSize: 1
    })

    const setFilter = (
        field: string,
        op: string,
        value: string | string[] | number | number[],
        e: React.MouseEvent<HTMLDivElement>
    ) => {
        e.preventDefault()
        if (!field) {
            logger.error("setFilter: Missing 'field' parameter.")
            return
        }

        const data = Array.isArray(value) ? value.map(String) : [String(value)]

        try {
            dispatch({ type: "SET_FILTER", payload: { field, op, data } })
        } catch (error) {
            logger.error(
                `setFilter: Error dispatching filter for field "${field}" with operation "${op}" and data:`,
                data,
                error
            )
            toast.error("An error occurred while applying the filter.")
        }
    }

    const clearFilter = (field: string, value: string) => {
        dispatch({
            type: "CLEAR_FILTER",
            payload: { field, value }
        })
    }

    const clearAllFilters = () => {
        dispatch({ type: "CLEAR_ALL_FILTERS" })
    }

    const clearSort = () => {
        dispatch({ type: "CLEAR_SORT" })
    }

    const setSort = (sortKey: keyof typeof sortConfig): void => {
        const sortOption = sortConfig[sortKey]
        if (!sortOption) {
            logger.error(
                `Invalid sort key: ${sortKey}. Available keys are: ${Object.keys(sortConfig).join(", ")}`
            )
            return
        }
        dispatch({ type: "SORT_PRODUCTS", payload: sortOption })
    }

    const totalPages = useMemo(() => {
        if (nHits <= fetchLimit) {
            return 1
        }
        return Math.ceil((nHits - fetchLimit) / loadMoreLimit) + 1
    }, [nHits, fetchLimit, loadMoreLimit])

    const products = useMemo(() => {
        if (!data || size === 1) {
            return groupedProducts
        }

        const isFilterOrSortActive =
            filterState.filters.length > 0 || filterState.sort !== undefined

        if (isFilterOrSortActive) {
            return data.flatMap(page => page.products)
        }

        // Skip groupedProducts (page 0)
        const fetchedProducts = data.slice(1).flatMap(page => page.products)

        const combinedProducts = groupedProducts?.length
            ? [...groupedProducts, ...fetchedProducts]
            : fetchedProducts

        return combinedProducts.slice(0, nHits)
    }, [filterState.filters.length, filterState.sort, data, groupedProducts, nHits, size])

    const isLoadingMore =
        isValidating && size > 0 && data && typeof data[size - 1] === "undefined"
    const isReachingEnd = size >= totalPages

    const searchParams = useSearchParams()
    const search = searchParams.toString()
    const scrollKey = `custom_scroll_${pathname}${search ? `?${search}` : ""}`
    const lastScrollY = useRef(0)

    useEffect(() => {
        if (typeof window === "undefined") return

        const savedScroll = sessionStorage.getItem(scrollKey)

        if (savedScroll && !isNaN(Number(savedScroll))) {
            const scrollPosition = parseInt(savedScroll, 10)

            if (scrollPosition > document.documentElement.scrollHeight) {
                window.scrollTo({ top: 0, behavior: "smooth" })
                sessionStorage.removeItem(scrollKey)
            } else {
                window.scrollTo({ top: scrollPosition })
                lastScrollY.current = scrollPosition
            }
        }

        const handleScroll = debounce(() => {
            if (window.scrollY > 0) {
                lastScrollY.current = Math.ceil(window.scrollY)
                sessionStorage.setItem(scrollKey, lastScrollY.current.toString())
            }
        }, 100)

        window.addEventListener("scroll", handleScroll)
        return () => {
            window.removeEventListener("scroll", handleScroll)
        }
    }, [scrollKey])

    const previousCategory = useRef(params.category)

    useEffect(() => {
        if (previousCategory.current !== params.category) {
            previousCategory.current = params.category
            setSize(1)
            mutate()
        }
    }, [params.category, mutate, setSize])

    const loadMore = (e: { preventDefault: () => void }) => {
        e.preventDefault()
        if (!isReachingEnd && !isLoadingMore) {
            setSize(size + 1)
        }
    }

    useEffect(() => {
        const hasFiltersChanged =
            JSON.stringify(prevFilterStateRef.current.filters) !==
            JSON.stringify(filterState.filters)
        const hasSortChanged =
            JSON.stringify(prevFilterStateRef.current.sort) !==
            JSON.stringify(filterState.sort)

        if (hasFiltersChanged || hasSortChanged) {
            prevFilterStateRef.current = filterState
            const updateData = async () => {
                await setSize(1)
                await mutate()
                updateQueryParamsWithSelectedOptions(
                    router,
                    pathname,
                    filterState.filters,
                    filterState.sort
                )
            }
            updateData()
        }
    }, [router, pathname, filterState, setSize, mutate])

    const isAnyOptionSelected = !(filterState.filters.length === 0 || !filterState?.sort)
    const amountOfSkeletons = fetchLimit - 8
    const skeletonArray = Array.from({ length: amountOfSkeletons })

    return (
        <section
            className={`grid-rows-auto-1fr row-gap-6 relative grid grid-cols-12 gap-x-15 overflow-hidden p-16 md:gap-x-24 md:p-24 md:pt-0 ${products?.length ?? 0 < 1 ? "min-h-[50vh]" : ""}`}
        >
            <DesktopFilter
                filters={initialFilters}
                selectedFiltersOptions={filterState}
                showFilter={showFilter}
                params={params}
                nHits={nHits}
                clearFilter={clearFilter}
                setFilter={setFilter}
                setSort={setSort}
                clearSort={clearSort}
                clearAllFilters={clearAllFilters}
            />
            <MobileFilter
                filters={initialFilters}
                selectedFiltersOptions={filterState}
                centraLangCode={centraLangCode}
                setFilter={setFilter}
                nHits={nHits}
                clearAllFilters={clearAllFilters}
            />
            <div className="col-span-12 grid grid-cols-12 gap-x-15 md:gap-x-24">
                {products.map((productGroup, index) => (
                    <Fragment key={`${productGroup.key}-${index}`}>
                        <div
                            key={index}
                            className={`col-span-${showFilter && index === 0 ? (isAnyOptionSelected ? "6 lg:col-span-3 min-[1260px]:col-span-3" : "12 md:col-span-12 lg:col-span-6") : "6 lg:col-span-3 min-[1260px]:col-span-3"}`}
                        >
                            {showFilter && index === 0 && !isAnyOptionSelected ? (
                                <SubBigProductCard
                                    lazyLoad
                                    productVariants={productGroup.items}
                                />
                            ) : (
                                <RegularProductCard
                                    lazyLoad={index > 4}
                                    productVariants={productGroup.items}
                                />
                            )}
                        </div>
                        {/* Render the CentraCategoryLinksContentCard after the 5th product (only on pcp lvl 1) */}
                        {(selectedCategoryChildren?.children?.length ?? 0) > 0 &&
                            !showFilter &&
                            index === 4 && (
                                <div className="col-span-6 lg:col-span-3 min-[1260px]:col-span-3">
                                    <CentraCategoryLinksContentCard
                                        paramsLocale={params.locale || "en"}
                                        selectedCategoryChildren={
                                            selectedCategoryChildren
                                        }
                                    />
                                </div>
                            )}
                    </Fragment>
                ))}
                {isLoading &&
                    (data?.length ?? 0) > 0 &&
                    skeletonArray.map((_, index) => (
                        <div
                            className="col-span-6 md:col-span-4 min-[1260px]:col-span-3"
                            key={index}
                        >
                            <Skeleton className="aspect-square w-full" />
                            <Skeleton className="mt-16 h-16 w-192 rounded-sm" />
                            <Skeleton className="mt-16 h-16 w-96 rounded-sm" />
                        </div>
                    ))}
                {/* little ugly this part: (isClient ? !isReachingEnd : true), but it enables this to work on both client and server without breaking the hydration */}
                {initialCursor && (isClient ? !isReachingEnd : true) && (
                    <LoadMoreCard
                        className="col-span-6 w-fit md:col-span-3"
                        onClick={e => loadMore(e)}
                        href={nextPageUrl}
                        title="Show more"
                    />
                )}
            </div>
        </section>
    )
}
